/**
 * Biopet (Pet Care Professional) report pages — the 10 ENG pages, shared by BOTH renderers:
 *
 *   • the Dashboard on-screen preview (React in the browser), and
 *   • the server-side PDF render (ReactDOMServer → renderPdfHtml.js → dist/renderer.cjs →
 *     Puppeteer), which is the same pipeline the BioMed panels use.
 *
 * This component was extracted out of DashboardPage.js precisely so the server could render
 * it. That means it MUST stay free of anything the server has no access to:
 *   - no component state, refs, effects or data fetching — every input arrives as a prop
 *   - no `localStorage` (the no-lacking supplement mode is passed in as `noLackingMode`)
 *   - no CSS custom properties (`var(--…)`) in inline styles. The dashboard theme sheets are
 *     not loaded in the Puppeteer document, so a `var()` would resolve to nothing there and
 *     the text would render transparent. The report is a fixed printed document anyway, so
 *     the palette below is hard-coded to the light-theme values it has always rendered with.
 */
import React, { useMemo } from 'react';
import {
  personalOverlayConfigBiopetEssential,
  formatBiopetOverlayDisplayValue
} from '../../configs/biopet_professional/Config_personal_biopet_professional';
import {
  BIOPET_ESSENTIAL_SPECIES_GROUPS,
  BIOPET_ESSENTIAL_PAGE4_POSITION,
  evaluateBiopetEssentialRow,
  getBiopetAgeStage
} from '../../configs/biopet_professional/calculate_biopet_professional';
import {
  BIOPET_ESSENTIAL_PAGE5_GRADE,
  BIOPET_ESSENTIAL_PAGE6_GRADE,
  BIOPET_ESSENTIAL_PAGE7_GRADE,
  BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT,
  biopetEssentialLabelToGradeFilename,
  buildBiopetPage8SupplementBarSegments
} from '../../configs/biopet_professional/Config_page_biopet_professional';
import {
  recommendBiopetEssentialSupplements,
  resolveBiopetSupplementSlots
} from '../../configs/biopet_professional/calculate_biopet_professional_supplement';
import { fontFamilyForMixedThaiLatin } from '../../utils/fontStacks';

/** Literal light-theme values of the tokens this report used to read via var(). */
const COLOR_TEXT_PRIMARY = '#174a5b';
const COLOR_TEXT_MUTED = '#4d7280';
const COLOR_SURFACE = '#ffffff';

export const BIOPET_REPORT_TOTAL_PAGES = 10;
export const BIOPET_PAGE_IMAGE_BASE = 'pdf_biopet_essential';

/* ---------------------------------------------------------------- page 4 */

function BiopetPage4ResultOverlay({ sectionTitle, rows, top, left }) {
  const barSegments = [
    'linear-gradient(90deg, #5969b1 0%, #80caeb 100%)',
    'linear-gradient(90deg, #1ba5c8 0%, #87ceb3 100%)',
    '#8dc63f',
    'linear-gradient(90deg, #febc11 0%, #f05773 100%)',
    'linear-gradient(90deg, #f05773 0%, #d11f43 100%)'
  ];

  const markerPercentByLabel = (item) => {
    if (item.group === 'harmful') {
      if (item.label === 'Excessive') return 90;
      if (item.label === 'Slightly excessive') return 70;
      return 50;
    }
    if (item.label === 'Deficient') return 10;
    if (item.label === 'Slightly deficient') return 30;
    return 50;
  };

  const contentHeight = useMemo(() => {
    if (!rows || rows.length === 0) return 0;
    const maxY = rows.reduce((acc, item) => {
      const yValues = [
        item?.position?.name?.y ?? 0,
        item?.position?.bar?.y ?? 0,
        item?.position?.label?.y ?? 0
      ];
      return Math.max(acc, ...yValues);
    }, 0);
    return Math.max(60, maxY + 44);
  }, [rows]);

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        width: 980,
        zIndex: 3
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 980,
          height: contentHeight
        }}
      >
        {rows.map((item) => (
          <div
            key={`${sectionTitle}-${item.speciesName}`}
            style={{
              position: 'relative',
              fontFamily: "'Gilroy', system-ui, sans-serif"
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: item?.position?.name?.x ?? 0,
                top: item?.position?.name?.y ?? 0,
                width: 340,
                fontSize: item?.position?.name?.fontSize ?? 18,
                fontStyle: item?.position?.name?.italic ? 'italic' : 'normal',
                color: COLOR_TEXT_PRIMARY,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={`${item.speciesName} (${item.label})`}
            >
              {item.speciesName}
            </div>
            <div
              style={{
                position: 'absolute',
                left: item?.position?.bar?.x ?? 360,
                top: item?.position?.bar?.y ?? 0,
                width: item?.position?.bar?.width ?? 420
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -10,
                  left: `${markerPercentByLabel(item)}%`,
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #424242'
                }}
              />
              <div
                style={{
                  marginTop: 2,
                  height: item?.position?.bar?.height ?? 24,
                  overflow: 'hidden',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 3,
                  background: COLOR_SURFACE,
                  padding: 0
                }}
              >
                {barSegments.map((segment, idx) => (
                  <div
                    key={`${item.speciesName}-seg-${idx}`}
                    style={{
                      background: segment
                    }}
                  />
                ))}
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                left: item?.position?.label?.x ?? 800,
                top: item?.position?.label?.y ?? 0,
                width: 170,
                textAlign: 'left',
                fontSize: item?.position?.label?.fontSize ?? 30,
                color: COLOR_TEXT_MUTED,
                lineHeight: 1,
                fontWeight: 400
              }}
            >
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ pages 5–7 */

function BiopetPage5GradeOverlay({ rows, top, left, width, gradeBasePath, basePath }) {
  const contentHeight = useMemo(() => {
    if (!rows || rows.length === 0) return 0;
    const maxY = rows.reduce((acc, item) => {
      const g = item?.gradePos || {};
      const bottom = (g.y ?? 0) + (g.height ?? 40);
      return Math.max(acc, bottom);
    }, 0);
    return Math.max(80, maxY + 24);
  }, [rows]);

  const base = `${basePath}/${gradeBasePath}`;

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        width: width ?? 980,
        zIndex: 3
      }}
    >
      <div
        style={{
          position: 'relative',
          width: width ?? 980,
          height: contentHeight
        }}
      >
        {rows.map((item) => {
          const file = biopetEssentialLabelToGradeFilename(item.label);
          const src = `${base}/${file}`;
          const gp = item.gradePos || {};
          const boxLeft = Math.round(gp.x ?? 0);
          const boxTop = Math.round(gp.y ?? 0);
          const boxWidth = Math.round(gp.width ?? 140);
          const boxHeight = Math.round(gp.height ?? 40);
          return (
            <div
              key={`page5-grade-${item.speciesName}`}
              style={{
                position: 'relative',
                fontFamily: "'Gilroy', system-ui, sans-serif"
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: boxLeft,
                  top: boxTop,
                  width: boxWidth,
                  height: boxHeight,
                  overflow: 'hidden',
                  display: 'block',
                  pointerEvents: 'none'
                }}
              >
                <img
                  src={src}
                  alt={item.label}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    display: 'block',
                    imageRendering: 'auto'
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- page 8 */

function biopetPage8JoinSupplementNames(names) {
  const arr = (names || []).filter(Boolean);
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;
}

/** Supplement images only — position varies by count via overlay / slotByCount. */
function BiopetPage8SupplementOverlay({
  supplements,
  top,
  left,
  width,
  height,
  baseWidth = 980,
  basePath,
  pathPrefix,
  imageBySupplement,
  slots,
  scaleSlotsWithOverlay = true
}) {
  const base = `${pathPrefix}/${basePath}`;
  const displaySupplements = (supplements || []).slice(0, (slots || []).length);
  const renderedWidth = width ?? baseWidth;
  const scale = baseWidth > 0 ? (renderedWidth / baseWidth) : 1;
  const slotScale = scaleSlotsWithOverlay ? scale : 1;

  const imageBottoms = displaySupplements.map((_, index) => {
    const s = slots?.[index] || {};
    return ((s.y ?? 0) + (s.height ?? 220)) * slotScale;
  });
  const maxImageBottom = imageBottoms.length ? Math.max(...imageBottoms) : 0;

  const computedHeight = Math.max(320 * slotScale, maxImageBottom + 20);
  const renderedHeight = Number.isFinite(height) ? height : computedHeight;
  const verticalScale =
    computedHeight > 0 && Number.isFinite(renderedHeight)
      ? (renderedHeight / computedHeight)
      : 1;

  return (
    <div
      className="pdf-overlay-image biopet-page8-supplement-overlay"
      style={{
        position: 'absolute',
        top,
        left,
        width: renderedWidth,
        zIndex: 3,
        pointerEvents: 'none'
      }}
    >
      <div
        style={{
          position: 'relative',
          width: renderedWidth,
          height: renderedHeight
        }}
      >
        {displaySupplements.map((name, index) => {
          const s = slots?.[index] || {};
          const file = imageBySupplement?.[name];
          if (!file) return null;
          const boxLeft = Math.round((s.x ?? 0) * slotScale);
          const boxTop = Math.round(((s.y ?? 0) * slotScale) * verticalScale);
          const boxWidth = Math.round((s.width ?? 220) * slotScale);
          const boxHeight = Math.round(((s.height ?? 220) * slotScale) * verticalScale);
          return (
            <div
              key={`supp-${name}-${index}`}
              className="pdf-overlay-image biopet-page8-supplement-slot"
              style={{
                position: 'absolute',
                left: boxLeft,
                top: boxTop,
                width: boxWidth,
                height: boxHeight,
                overflow: 'hidden',
                display: 'block',
                pointerEvents: 'none'
              }}
              title={name}
            >
              <img
                src={`${base}/${file}`}
                alt={name}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  // Keep slot height identical between preview and html2canvas capture.
                  // `contain` can render shorter height in captured output.
                  objectFit: 'fill',
                  display: 'block',
                  imageRendering: 'auto'
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Recommendation sentence — own layer; same page position for 1/2/3 supplements. */
function BiopetPage8RecommendationTextOverlay({
  supplements,
  slots,
  top,
  left,
  width,
  baseWidth = 980,
  recommendationText,
  scaleRecommendationTextWithOverlay = false,
  zIndex = 4
}) {
  const displaySupplements = (supplements || []).slice(0, (slots || []).length);
  if (recommendationText?.enabled === false || displaySupplements.length === 0) {
    return null;
  }

  const sentence = `${recommendationText?.prefix || 'According to the analysis result, it is recommended to use '}${biopetPage8JoinSupplementNames(displaySupplements)}`;
  const renderedWidth = width ?? baseWidth;
  const scale = baseWidth > 0 ? (renderedWidth / baseWidth) : 1;
  const textScale = scaleRecommendationTextWithOverlay ? scale : 1;

  const fontScale =
    recommendationText?.scaleFontWithOverlay && scaleRecommendationTextWithOverlay
      ? textScale
      : 1;
  const fontSizePx = (recommendationText?.fontSize ?? 22) * fontScale;
  const lineHeight = recommendationText?.lineHeight ?? 1.25;

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        width: renderedWidth,
        zIndex
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: (recommendationText?.x ?? 0) * textScale,
          top: (recommendationText?.y ?? 0) * textScale,
          width: (recommendationText?.width ?? baseWidth) * textScale,
          fontSize: fontSizePx,
          color: recommendationText?.color || '#1d2a35',
          fontFamily: recommendationText?.fontFamily || "'Gilroy', system-ui, sans-serif",
          fontWeight: recommendationText?.fontWeight ?? 500,
          lineHeight,
          textAlign: recommendationText?.textAlign || 'center'
        }}
      >
        {sentence}
      </div>
    </div>
  );
}

/** Page 8: row of boxes (e.g. 6) cycling through recommended supplement names. */
function BiopetPage8SupplementBarOverlay({ supplements, barConfig }) {
  if (!barConfig || barConfig.enabled === false) return null;
  const list = (supplements || []).filter(Boolean);
  const segmentCount = barConfig.segmentCount ?? 6;
  const segments = buildBiopetPage8SupplementBarSegments(list, segmentCount);
  if (!segments.length) return null;

  const o = barConfig.overlay || {};
  const outerTop = o.top ?? 640;
  const outerLeft = o.left ?? 125;
  const outerW = o.width ?? 980;
  const z = o.zIndex ?? 4;
  const innerX = barConfig.x ?? 0;
  const innerY = barConfig.y ?? 0;
  const barWidth = barConfig.barWidth ?? Math.max(100, outerW - innerX);
  const gap = barConfig.gap ?? 8;
  const segmentHeight = barConfig.segmentHeight ?? 44;
  const borderRadius = barConfig.borderRadius ?? 4;
  const fontSize = barConfig.fontSize ?? 14;
  const fontWeight = barConfig.fontWeight ?? 700;
  const fontFamily = barConfig.fontFamily || "'Gilroy', system-ui, sans-serif";
  const stylesMap = barConfig.supplementStyles || {};
  const defaultStyle = barConfig.defaultStyle || {
    backgroundColor: '#64748b',
    color: '#ffffff'
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: outerTop,
        left: outerLeft,
        width: outerW,
        zIndex: z
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: innerX,
          top: innerY,
          width: barWidth,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap
        }}
      >
        {segments.map((name, idx) => {
          const spec = stylesMap[name] || defaultStyle;
          const label = spec.displayLabel ?? name;
          return (
            <div
              key={`supp-bar-${name}-${idx}`}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                height: segmentHeight,
                borderRadius,
                backgroundColor: spec.backgroundColor ?? defaultStyle.backgroundColor,
                color: spec.color ?? defaultStyle.color,
                fontSize,
                fontWeight,
                fontFamily,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '0 4px',
                boxSizing: 'border-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={label}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- data (pure) */

/**
 * Everything the 10 pages need, derived from the report row alone.
 *
 * Was a chain of useMemo hooks inside DashboardPage; now a plain function so the server
 * can call it too. `manualSupplements` (the user's Plan-C override) wins over the
 * quartile-derived recommendation, exactly as it did on screen.
 */
export function computeBiopetReportData({
  formData,
  quartileBySpecies,
  manualSupplements = null,
  noLackingMode = null,
  stockSnapshot = null
}) {
  const ageStage = getBiopetAgeStage(formData?.age);
  const universal = formData?.universal ?? formData?.['Universal bacteria'] ?? 0;

  const evaluate = (speciesName, group, speciesIndex = 0) =>
    evaluateBiopetEssentialRow({
      speciesName,
      group,
      speciesIndex,
      speciesValue: formData?.[speciesName],
      universalValue: universal,
      ageStage,
      quartileBySpecies,
      fallbackQ2: 0,
      fallbackQ3: 0
    });

  const evaluateGroup = (groupName) =>
    (BIOPET_ESSENTIAL_SPECIES_GROUPS[groupName] || []).map((speciesName) =>
      evaluate(speciesName, groupName)
    );

  const page4Rows = {
    ageStage,
    probiotics: evaluateGroup('probiotics'),
    core: evaluateGroup('core'),
    harmful: evaluateGroup('harmful')
  };

  const gradeRowsFor = (config) =>
    (config.species || []).map((spec) => {
      const evaluated = evaluate(spec.speciesName, spec.group, spec.speciesIndex ?? 0);
      return {
        speciesName: evaluated.speciesName,
        label: evaluated.label,
        gradePos: spec.grade
      };
    });

  const supplements =
    Array.isArray(manualSupplements) && manualSupplements.length > 0
      ? manualSupplements
      : (recommendBiopetEssentialSupplements(page4Rows.probiotics || [], {
          noLackingMode,
          stockSnapshot
        }).recommendedSupplements || []);

  const page8Slots = resolveBiopetSupplementSlots(
    supplements,
    BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.slots || [],
    BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.slotByCount || null
  );

  const overlayCount = Math.min(Math.max((supplements || []).length, 0), 3);
  const overlayByCount = BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.overlayByCount || {};
  const page8Overlay =
    overlayByCount[overlayCount] || BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.overlay || {};

  const ro = BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.recommendationOverlay;
  const o = BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.overlay || {};
  const page8RecommendationOverlay = ro
    ? {
        top: ro.top ?? 603,
        left: ro.left ?? 125,
        width: ro.width ?? 980,
        zIndex: ro.zIndex ?? 4
      }
    : {
        top: (o.top ?? 520) + 83,
        left: (o.left ?? 120) + 5,
        width: o.width ?? 980,
        zIndex: 4
      };

  return {
    page4Rows,
    page5GradeRows: gradeRowsFor(BIOPET_ESSENTIAL_PAGE5_GRADE),
    page6GradeRows: gradeRowsFor(BIOPET_ESSENTIAL_PAGE6_GRADE),
    page7GradeRows: gradeRowsFor(BIOPET_ESSENTIAL_PAGE7_GRADE),
    supplements,
    page8Slots,
    page8Overlay,
    page8RecommendationOverlay
  };
}

/* ------------------------------------------------------------ pages */

/**
 * The 10 report pages. `data` is the result of computeBiopetReportData; pass it in when the
 * caller already has it (the dashboard memoises it), otherwise it is computed here.
 *
 * `basePath` prefixes every asset URL: '' on the server (Puppeteer resolves /pdf_biopet_essential
 * against the Express static mount) and PUBLIC_URL in the browser.
 */
export default function BiopetReportPages({
  formData,
  quartileBySpecies,
  data = null,
  basePath = process.env.PUBLIC_URL || '',
  manualSupplements = null,
  noLackingMode = null,
  stockSnapshot = null,
  pageStyle = null
}) {
  const resolved =
    data ||
    computeBiopetReportData({
      formData,
      quartileBySpecies,
      manualSupplements,
      noLackingMode,
      stockSnapshot
    });

  const {
    page4Rows,
    page5GradeRows,
    page6GradeRows,
    page7GradeRows,
    supplements,
    page8Slots,
    page8Overlay,
    page8RecommendationOverlay
  } = resolved;

  return (
    <>
      {Array.from({ length: BIOPET_REPORT_TOTAL_PAGES }, (_, idx) => (
        <div
          id={`pet-professional-page-${idx + 1}`}
          key={idx + 1}
          className="pdf-page-wrapper"
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: COLOR_SURFACE,
            ...(pageStyle || {})
          }}
        >
          <img
            src={`${basePath}/${BIOPET_PAGE_IMAGE_BASE}/${idx + 1}.jpg`}
            alt={`Pet Care Professional Page ${idx + 1}`}
            className="pdf-page-img"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
          {personalOverlayConfigBiopetEssential.eng.fields
            .filter((field) => {
              const targetPage = field.page ?? personalOverlayConfigBiopetEssential.eng.page;
              return (idx + 1) === targetPage;
            })
            .map((field) => {
              const raw = formData?.[field.key];
              if (raw === undefined || raw === null || String(raw).trim() === '') {
                return null;
              }
              const displayValue = formatBiopetOverlayDisplayValue(field, raw);

              return (
                <div
                  key={field.key}
                  className="pdf-overlay-text"
                  style={{
                    position: 'absolute',
                    left: `${field.x}px`,
                    top: `${field.y}px`,
                    fontSize: `${field.fontSize || 14}px`,
                    color: COLOR_TEXT_PRIMARY,
                    fontFamily:
                      field.fontFamily ?? fontFamilyForMixedThaiLatin(displayValue),
                    lineHeight: 1.2,
                    zIndex: 2
                  }}
                >
                  {displayValue}
                </div>
              );
            })}
          {(idx + 1) === (BIOPET_ESSENTIAL_PAGE4_POSITION.page || 4) && (
            <>
              <BiopetPage4ResultOverlay
                sectionTitle="Probiotics"
                rows={page4Rows.probiotics}
                top={525}
                left={120}
              />
              <BiopetPage4ResultOverlay
                sectionTitle="Core bacteria"
                rows={page4Rows.core}
                top={1030}
                left={120}
              />
              <BiopetPage4ResultOverlay
                sectionTitle="Harmful bacteria"
                rows={page4Rows.harmful}
                top={1250}
                left={120}
              />
            </>
          )}
          {(idx + 1) === (BIOPET_ESSENTIAL_PAGE5_GRADE.page || 5) && (
            <BiopetPage5GradeOverlay
              rows={page5GradeRows}
              top={BIOPET_ESSENTIAL_PAGE5_GRADE.overlay?.top ?? 525}
              left={BIOPET_ESSENTIAL_PAGE5_GRADE.overlay?.left ?? 120}
              width={BIOPET_ESSENTIAL_PAGE5_GRADE.overlay?.width ?? 980}
              gradeBasePath={
                BIOPET_ESSENTIAL_PAGE5_GRADE.gradeBasePath || 'biopet_essentialgrade'
              }
              basePath={basePath}
            />
          )}
          {(idx + 1) === (BIOPET_ESSENTIAL_PAGE6_GRADE.page || 6) && (
            <BiopetPage5GradeOverlay
              rows={page6GradeRows}
              top={BIOPET_ESSENTIAL_PAGE6_GRADE.overlay?.top ?? 525}
              left={BIOPET_ESSENTIAL_PAGE6_GRADE.overlay?.left ?? 120}
              width={BIOPET_ESSENTIAL_PAGE6_GRADE.overlay?.width ?? 980}
              gradeBasePath={
                BIOPET_ESSENTIAL_PAGE6_GRADE.gradeBasePath || 'biopet_essentialgrade'
              }
              basePath={basePath}
            />
          )}
          {(idx + 1) === (BIOPET_ESSENTIAL_PAGE7_GRADE.page || 7) && (
            <BiopetPage5GradeOverlay
              rows={page7GradeRows}
              top={BIOPET_ESSENTIAL_PAGE7_GRADE.overlay?.top ?? 525}
              left={BIOPET_ESSENTIAL_PAGE7_GRADE.overlay?.left ?? 120}
              width={BIOPET_ESSENTIAL_PAGE7_GRADE.overlay?.width ?? 980}
              gradeBasePath={
                BIOPET_ESSENTIAL_PAGE7_GRADE.gradeBasePath || 'biopet_essentialgrade'
              }
              basePath={basePath}
            />
          )}
          {(idx + 1) === (BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.page || 8) && (
            <>
              <BiopetPage8SupplementOverlay
                supplements={supplements}
                top={page8Overlay.top ?? 520}
                left={page8Overlay.left ?? 120}
                width={page8Overlay.width ?? 980}
                height={page8Overlay.height}
                baseWidth={BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.baseWidth ?? 980}
                basePath={
                  BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.supplementBasePath ||
                  'supp_biopet_professional'
                }
                pathPrefix={basePath}
                imageBySupplement={BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.imageBySupplement}
                slots={page8Slots}
                scaleSlotsWithOverlay={
                  BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.scaleSlotsWithOverlay !== false
                }
              />
              <BiopetPage8SupplementBarOverlay
                supplements={supplements}
                barConfig={BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.supplementBar}
              />
              <BiopetPage8RecommendationTextOverlay
                supplements={supplements}
                slots={page8Slots}
                top={page8RecommendationOverlay.top ?? 603}
                left={page8RecommendationOverlay.left ?? 125}
                width={page8RecommendationOverlay.width ?? 980}
                baseWidth={BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.baseWidth ?? 980}
                recommendationText={BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.recommendationText}
                scaleRecommendationTextWithOverlay={
                  BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT.scaleRecommendationTextWithOverlay ===
                  true
                }
                zIndex={page8RecommendationOverlay.zIndex ?? 4}
              />
            </>
          )}
        </div>
      ))}
    </>
  );
}
